import { useEffect, useRef, useState } from 'react';
import useSWR from 'swr';

import { apiRoutes, getApiUrl } from '@/constants/api';
import { useSearchParamsContext } from '@/contexts/search-params-context';
import { clientApiFetcher } from '@/lib/api/fetcher/client';

export type Location = {
  _id: string;
  cityName: string;
  latitude: number;
  longitude: number;
  province: string;
  provinceCode: string;
};

const DEBOUNCE_TIME = 600;

export const useLocationFilter = () => {
  const { filters, updateFilter } = useSearchParamsContext();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [locations, setLocations] = useState<string[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const value = filters.location || 'any';

  const { data, isLoading, error } = useSWR<Location[]>(
    debouncedQuery.length >= 2
      ? getApiUrl(apiRoutes.post.getLocations(debouncedQuery))
      : null,
    clientApiFetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      revalidateOnReconnect: false,
      dedupingInterval: 10000,
      keepPreviousData: false,
      shouldRetryOnError: false,
      focusThrottleInterval: 5000,
      onSuccess: () => setIsSearching(false),
      onError: () => setIsSearching(false),
    },
  );

  useEffect(() => {
    if (data && debouncedQuery.length >= 2) {
      setLocations(data.map((loc) => loc.cityName));
    }
  }, [data, debouncedQuery]);

  useEffect(() => {
    if (isSearching && data && !isLoading) {
      setIsSearching(false);
    }
  }, [isSearching, data, isLoading]);

  const setValue = (newValue: string | null) => {
    if (newValue === 'any' || newValue === null) {
      updateFilter('location', undefined);
    } else {
      updateFilter('location', newValue);
    }
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (value.length < 2) {
      setDebouncedQuery('');
      setLocations([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);

    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value);
    }, DEBOUNCE_TIME);
  };

  const clearLocation = () => {
    setValue('any');
    setSearchQuery('');
    setDebouncedQuery('');
    setLocations([]);
    setIsSearching(false);
  };

  return {
    value,
    setValue,
    searchQuery,
    setSearchQuery,
    locations,
    isPending: isLoading,
    isSearching,
    error,
    handleSearch,
    clearLocation,
  };
};
